<template>
    <div class="bg-white p-6 rounded-lg border">
        <h3 class="text-lg font-medium text-gray-900 mb-4 text-center">Estado de tareas</h3>
        <div class="flex justify-center items-center h-[calc(100%-3rem)] space-x-12">
            <!-- Vencidas -->
            <div class="flex flex-col items-center space-y-2">
                <div class="w-12 h-12 relative">
                    <svg class="w-12 h-12 text-red-500" viewBox="0 0 24 24">
                        <circle 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            stroke-width="4" 
                            fill="none" 
                            class="opacity-25"
                        />
                        <path 
                            :d="describeArc(12, 12, 8, 0, porcentajeVencidas)"
                            fill="currentColor"
                            class="opacity-75"
                        />
                    </svg>
                </div>
                <span class="text-gray-600 font-medium">Vencidas</span>
                <span class="text-red-600 font-bold text-lg">{{ vencidas }}</span>
            </div>

            <!-- Por vencer -->
            <div class="flex flex-col items-center space-y-2">
                <div class="w-12 h-12 relative">
                    <svg class="w-12 h-12 text-yellow-500" viewBox="0 0 24 24">
                        <circle 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            stroke-width="4" 
                            fill="none" 
                            class="opacity-25"
                        />
                        <path 
                            :d="describeArc(12, 12, 8, 0, porcentajePorVencer)"
                            fill="currentColor"
                            class="opacity-75"
                        />
                    </svg>
                </div>
                <span class="text-gray-600 font-medium">Por vencer</span>
                <span class="text-yellow-600 font-bold text-lg">{{ porVencer }}</span>
            </div>

            <!-- A tiempo -->
            <div class="flex flex-col items-center space-y-2">
                <div class="w-12 h-12 relative">
                    <svg class="w-12 h-12 text-green-500" viewBox="0 0 24 24">
                        <circle 
                            cx="12" 
                            cy="12" 
                            r="10" 
                            stroke="currentColor" 
                            stroke-width="4" 
                            fill="none" 
                            class="opacity-25"
                        />
                        <path 
                            :d="describeArc(12, 12, 8, 0, porcentajeATiempo)"
                            fill="currentColor"
                            class="opacity-75"
                        />
                    </svg>
                </div>
                <span class="text-gray-600 font-medium">A tiempo</span>
                <span class="text-green-600 font-bold text-lg">{{ aTiempo }}</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue';
import type ITask from '@/interfaces/ITask';

const props = defineProps({
    items: {
        type: Array as () => Array<ITask>,
        required: true
    }
});

// Función auxiliar para convertir grados a radianes
const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
        x: centerX + (radius * Math.cos(angleInRadians)),
        y: centerY + (radius * Math.sin(angleInRadians))
    };
}

// Función para crear el path del arco SVG
const describeArc = (x: number, y: number, radius: number, startAngle: number, endAngle: number) => {
    // Si el ángulo es 360 grados (círculo completo), usar un path diferente
    if (endAngle >= 359.9) {
        return [
            "M", x + radius, y,
            "A", radius, radius, 0, 1, 0, x - radius, y,
            "A", radius, radius, 0, 1, 0, x + radius, y,
            "Z"
        ].join(" ");
    }

    const start = polarToCartesian(x, y, radius, endAngle);
    const end = polarToCartesian(x, y, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
    
    return [
        "M", start.x, start.y,
        "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
        "L", x, y,
        "Z"
    ].join(" ");
}

const comprobarVencimiento = (date: Date) => {
    const fecha = new Date(date);
    const hoy = new Date();
    const casiVencida = new Date(fecha);
    casiVencida.setDate(casiVencida.getDate() - 1);

    if (fecha.getDate() === 1 && fecha.getMonth() === 0 && fecha.getFullYear() === 1900){
        return "en_tiempo";
    }

    if (fecha < hoy){
        return "vencida";
    } else if (casiVencida <= hoy){
        return "casi_vencida";
    } else {
        return "en_tiempo";
    }
}

const totalTareas = computed(() => props.items.length);

const vencidas = computed(() => 
    props.items.filter(item => 
        comprobarVencimiento(item.deadline ?? new Date()) === 'vencida'
    ).length
);

const porVencer = computed(() => 
    props.items.filter(item => 
        comprobarVencimiento(item.deadline ?? new Date()) === 'casi_vencida'
    ).length
);

const aTiempo = computed(() => 
    props.items.filter(item => 
        comprobarVencimiento(item.deadline ?? new Date()) === 'en_tiempo'
    ).length
);

// Calcular porcentajes para los arcos
const porcentajeVencidas = computed(() => 
    totalTareas.value > 0 ? (vencidas.value / totalTareas.value) * 360 : 0
);

const porcentajePorVencer = computed(() => 
    totalTareas.value > 0 ? (porVencer.value / totalTareas.value) * 360 : 0
);

const porcentajeATiempo = computed(() => 
    totalTareas.value > 0 ? (aTiempo.value / totalTareas.value) * 360 : 0
);
</script> 